/* ============================================================
   PropertyInspector — Dense IDE property panel
   ─────────────────────────────────────────────────────────
   Collapsible sections: PRESETS · CONTENT · LAYOUT · DATA BINDING
   Quick-pick preset chips for common values
   Dense form controls with `.editor-*` CSS classes
   ============================================================ */

import { useState, useCallback, useMemo } from 'react'
import { Settings2, Copy, Trash2, ChevronRight, RotateCcw, Palette } from 'lucide-react'
import { getCardDef, type CanvasBlock } from '@/lib/cms/card-registry'

interface PropertyInspectorProps {
  block: CanvasBlock | null
  onUpdateBlock: (id: string, data: Record<string, string>) => void
  onResizeBlock: (id: string, colSpan: number) => void
  onRemoveBlock: (id: string) => void
  onDuplicateBlock: (id: string) => void
}

/* ── Field metadata ────────────────────────────────── */

const FIELD_LABELS: Record<string, string> = {
  headline: 'Headline',
  subtitle: 'Subtitle',
  heading: 'Heading',
  body: 'Body Text',
  name: 'Name',
  title: 'Title',
  price: 'Price',
  duration: 'Duration (min)',
  description: 'Description',
  quote: 'Quote',
  author: 'Author',
  rating: 'Rating (1-5)',
  ctaText: 'Button Text',
  buttonText: 'Button Text',
  bgImage: 'Background Image URL',
  src: 'Image URL',
  alt: 'Alt Text',
  caption: 'Caption',
  date: 'Date',
  time: 'Time',
  location: 'Location',
  height: 'Height (px)',
  text: 'Text',
  level: 'Heading Level',
  align: 'Alignment',
  style: 'Style',
}

const TEXTAREA_FIELDS = new Set(['body', 'description', 'quote', 'subtitle', 'caption'])

const SELECT_OPTIONS: Record<string, { value: string; label: string }[]> = {
  level: [
    { value: 'h1', label: 'H1 — Page' },
    { value: 'h2', label: 'H2 — Section' },
    { value: 'h3', label: 'H3 — Sub' },
    { value: 'h4', label: 'H4 — Minor' },
  ],
  align: [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ],
  style: [
    { value: 'default', label: 'Default' },
    { value: 'outlined', label: 'Outlined' },
    { value: 'gradient', label: 'Gradient' },
    { value: 'glass', label: 'Glass' },
  ],
}

/* ── Value Presets ──────────────────────────────────── */

interface PresetChip {
  label: string
  value: string
  color?: string
}

const COLOR_PRESETS: PresetChip[] = [
  { label: 'Gold', value: '#C9A84C', color: '#C9A84C' },
  { label: 'Indigo', value: '#6366f1', color: '#6366f1' },
  { label: 'Emerald', value: '#34d399', color: '#34d399' },
  { label: 'Rose', value: '#f43f5e', color: '#f43f5e' },
  { label: 'Slate', value: '#64748b', color: '#64748b' },
]

const SPACING_PRESETS: PresetChip[] = [
  { label: 'Compact', value: '120' },
  { label: 'Default', value: '200' },
  { label: 'Spacious', value: '320' },
  { label: 'Tall', value: '480' },
]

const STYLE_PRESETS: PresetChip[] = [
  { label: 'Default', value: 'default' },
  { label: 'Outlined', value: 'outlined' },
  { label: 'Gradient', value: 'gradient' },
  { label: 'Glass', value: 'glass' },
]

// Map field keys to their preset group
const FIELD_PRESETS: Record<string, PresetChip[]> = {
  style: STYLE_PRESETS,
  height: SPACING_PRESETS,
}

// Color-related field keys
const COLOR_FIELDS = new Set(['bgImage', 'backgroundColor', 'textColor', 'borderColor'])

/* ── Component ───────────────────────────────────────── */

export default function PropertyInspector({
  block,
  onUpdateBlock,
  onResizeBlock,
  onRemoveBlock,
  onDuplicateBlock,
}: PropertyInspectorProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    content: true,
    presets: true,
  })

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleFieldChange = useCallback(
    (key: string, value: string) => {
      if (!block) return
      onUpdateBlock(block.id, { ...block.data, [key]: value })
    },
    [block, onUpdateBlock]
  )

  const def = useMemo(() => (block ? getCardDef(block.type) : null), [block])

  // Reset a field to its default
  const handleResetField = useCallback(
    (key: string) => {
      if (!block || !def) return
      const defaultValue = def.defaults[key] ?? ''
      onUpdateBlock(block.id, { ...block.data, [key]: defaultValue })
    },
    [block, def, onUpdateBlock]
  )

  // Reset all fields to defaults
  const handleResetAll = useCallback(() => {
    if (!block || !def) return
    onUpdateBlock(block.id, { ...def.defaults })
  }, [block, def, onUpdateBlock])

  /* ── Empty state ── */
  if (!block) {
    return (
      <div className="editor-empty-state">
        <Settings2 size={24} className="editor-empty-state-icon" />
        <div className="editor-empty-state-text">No block selected</div>
        <div className="editor-empty-state-hint">Click a block on the canvas to inspect</div>
      </div>
    )
  }

  const dataKeys = Object.keys(block.data)
  const hasModifiedFields = def
    ? dataKeys.some((k) => block.data[k] !== (def.defaults[k] ?? ''))
    : false

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Block identity bar */}
      <div
        style={{
          padding: '8px var(--editor-panel-pad)',
          borderBottom: '1px solid var(--editor-border-dim)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ fontSize: 14 }}>{def?.icon || '📄'}</span>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--editor-font-size)',
              fontWeight: 600,
              color: 'var(--editor-text)',
            }}
          >
            {def?.label || block.type}
          </div>
          <div
            style={{
              fontSize: 'var(--editor-font-size-xs)',
              fontFamily: 'var(--editor-font-mono)',
              color: 'var(--editor-text-dim)',
            }}
          >
            id:{block.id.slice(-8)} · {block.colSpan}/12
          </div>
        </div>
        {/* Reset all button */}
        {hasModifiedFields && (
          <button
            className="editor-btn editor-btn-ghost editor-btn-sm"
            onClick={handleResetAll}
            title="Reset all to defaults"
            style={{ padding: '3px 6px', flexShrink: 0 }}
          >
            <RotateCcw size={11} />
          </button>
        )}
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* ── QUICK PRESETS ── */}
        <SectionHeader
          title="Quick Presets"
          open={!!expanded.presets}
          onToggle={() => toggle('presets')}
          badge="new"
          icon={<Palette size={11} />}
        />
        {expanded.presets && (
          <div className="editor-section-body">
            {/* Style presets */}
            <div className="editor-row">
              <label className="editor-label">Style</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {STYLE_PRESETS.map((preset) => (
                  <PresetChipButton
                    key={preset.value}
                    preset={preset}
                    isActive={block.data.style === preset.value}
                    onClick={() => handleFieldChange('style', preset.value)}
                  />
                ))}
              </div>
            </div>

            {/* Color presets */}
            <div className="editor-row">
              <label className="editor-label">Theme Colors</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => {
                      // Apply to first available color-like field, or bgImage
                      const colorKey = dataKeys.find((k) => COLOR_FIELDS.has(k)) || 'bgImage'
                      if (dataKeys.includes(colorKey)) {
                        handleFieldChange(colorKey, preset.value)
                      }
                    }}
                    title={preset.label}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      border: '2px solid transparent',
                      background: preset.color,
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease, border-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.15)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  />
                ))}
              </div>
            </div>

            {/* Spacing presets (show only if height field exists) */}
            {dataKeys.includes('height') && (
              <div className="editor-row">
                <label className="editor-label">Height</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {SPACING_PRESETS.map((preset) => (
                    <PresetChipButton
                      key={preset.value}
                      preset={preset}
                      isActive={block.data.height === preset.value}
                      onClick={() => handleFieldChange('height', preset.value)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Alignment presets */}
            {dataKeys.includes('align') && (
              <div className="editor-row">
                <label className="editor-label">Alignment</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {SELECT_OPTIONS.align.map((opt) => (
                    <PresetChipButton
                      key={opt.value}
                      preset={{ label: opt.label, value: opt.value }}
                      isActive={block.data.align === opt.value}
                      onClick={() => handleFieldChange('align', opt.value)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CONTENT ── */}
        <SectionHeader
          title="Content"
          open={!!expanded.content}
          onToggle={() => toggle('content')}
        />
        {expanded.content && (
          <div className="editor-section-body">
            {dataKeys.map((key) => {
              const fieldPresets = FIELD_PRESETS[key]
              const isModified = def && block.data[key] !== (def.defaults[key] ?? '')

              return (
                <div key={key} className="editor-row">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <label className="editor-label" style={{ marginBottom: 0 }}>
                      {FIELD_LABELS[key] || key}
                    </label>
                    {isModified && (
                      <button
                        onClick={() => handleResetField(key)}
                        title="Reset to default"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0 2px',
                          color: 'var(--editor-text-dim)',
                          opacity: 0.5,
                          transition: 'opacity 0.15s',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
                      >
                        <RotateCcw size={10} />
                      </button>
                    )}
                  </div>
                  {/* Preset chips for this field */}
                  {fieldPresets && (
                    <div style={{ display: 'flex', gap: 3, marginBottom: 4, flexWrap: 'wrap' }}>
                      {fieldPresets.map((preset) => (
                        <PresetChipButton
                          key={preset.value}
                          preset={preset}
                          isActive={block.data[key] === preset.value}
                          onClick={() => handleFieldChange(key, preset.value)}
                          size="sm"
                        />
                      ))}
                    </div>
                  )}
                  {SELECT_OPTIONS[key] ? (
                    <select
                      className="editor-input"
                      value={block.data[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                    >
                      {SELECT_OPTIONS[key].map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : TEXTAREA_FIELDS.has(key) ? (
                    <textarea
                      className="editor-input"
                      value={block.data[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      rows={3}
                      style={{ resize: 'vertical' }}
                    />
                  ) : (
                    <input
                      type="text"
                      className="editor-input"
                      value={block.data[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* ── LAYOUT ── */}
        <SectionHeader title="Layout" open={!!expanded.layout} onToggle={() => toggle('layout')} />
        {expanded.layout && (
          <div className="editor-section-body">
            <div className="editor-row">
              <label className="editor-label">Column Span</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {(def?.allowedColSpans || [3, 4, 6, 12]).map((span) => (
                  <button
                    key={span}
                    onClick={() => onResizeBlock(block.id, span)}
                    className={`editor-btn editor-btn-sm ${
                      block.colSpan === span ? 'editor-btn-primary' : 'editor-btn-ghost'
                    }`}
                    style={{
                      flex: 1,
                      fontFamily: 'var(--editor-font-mono)',
                      fontWeight: 700,
                      fontSize: 10,
                    }}
                  >
                    {span}
                  </button>
                ))}
              </div>
            </div>

            <div className="editor-row">
              <label htmlFor="pi-display" className="editor-label">
                Display
              </label>
              <select id="pi-display" className="editor-input" disabled>
                <option>Flex</option>
              </select>
            </div>
            <div className="editor-row">
              <label htmlFor="pi-direction" className="editor-label">
                Direction
              </label>
              <select id="pi-direction" className="editor-input" disabled>
                <option>Column</option>
              </select>
            </div>
            <div className="editor-row">
              <label htmlFor="pi-align" className="editor-label">
                Align
              </label>
              <select id="pi-align" className="editor-input" disabled>
                <option>Flex Start</option>
              </select>
            </div>
            <div className="editor-row">
              <label htmlFor="pi-gap" className="editor-label">
                Gap
              </label>
              <input
                id="pi-gap"
                type="text"
                className="editor-input"
                defaultValue="12"
                disabled
                style={{ fontFamily: 'var(--editor-font-mono)' }}
              />
            </div>
          </div>
        )}

        {/* ── DIMENSIONS ── */}
        <SectionHeader
          title="Dimensions"
          open={!!expanded.dimensions}
          onToggle={() => toggle('dimensions')}
        />
        {expanded.dimensions && (
          <div className="editor-section-body">
            <div className="editor-row">
              <label htmlFor="pi-width" className="editor-label">
                Width
              </label>
              <input
                id="pi-width"
                type="text"
                className="editor-input"
                value={
                  block.colSpan === 12 ? '100' : String(Math.round((block.colSpan / 12) * 100))
                }
                disabled
                style={{ fontFamily: 'var(--editor-font-mono)' }}
              />
            </div>
            <div className="editor-row">
              <label htmlFor="pi-min-height" className="editor-label">
                Min Height
              </label>
              <input
                id="pi-min-height"
                type="text"
                className="editor-input"
                defaultValue="auto"
                style={{ fontFamily: 'var(--editor-font-mono)' }}
                disabled
              />
            </div>
            <div className="editor-row">
              <label htmlFor="pi-padding" className="editor-label">
                Padding
              </label>
              <input
                id="pi-padding"
                type="text"
                className="editor-input"
                defaultValue="40 32 40 32"
                style={{ fontFamily: 'var(--editor-font-mono)' }}
                disabled
              />
            </div>

            {/* Box model visualizer */}
            <div
              style={{
                margin: '8px 0',
                border: '1px solid var(--editor-accent-dim)',
                borderRadius: 'var(--editor-radius)',
                padding: 8,
                position: 'relative',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontSize: 8,
                  color: 'var(--editor-text-dim)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  marginBottom: 2,
                }}
              >
                margin
              </div>
              <div
                style={{
                  border: '1px dashed var(--editor-accent-dim)',
                  borderRadius: 3,
                  padding: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 8,
                    color: 'var(--editor-text-dim)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    marginBottom: 2,
                  }}
                >
                  padding
                </div>
                <div
                  style={{
                    background: 'var(--editor-accent-dim)',
                    borderRadius: 2,
                    padding: '6px 12px',
                    fontSize: 'var(--editor-font-size-xs)',
                    fontFamily: 'var(--editor-font-mono)',
                    color: 'var(--editor-text-dim)',
                  }}
                >
                  {Math.round((block.colSpan / 12) * 1440)} × auto
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  position: 'absolute',
                  left: 4,
                  right: 4,
                  bottom: 4,
                  fontSize: 8,
                  fontFamily: 'var(--editor-font-mono)',
                  color: 'var(--editor-text-dim)',
                }}
              >
                <span>0</span>
                <span>40</span>
                <span>0</span>
              </div>
            </div>
          </div>
        )}

        {/* ── FILL ── */}
        <SectionHeader
          title="Fill"
          open={!!expanded.fill}
          onToggle={() => toggle('fill')}
          badge="theme"
        />
        {expanded.fill && (
          <div className="editor-section-body">
            <div className="editor-row">
              <label htmlFor="pi-background" className="editor-label">
                Background
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: '1px solid var(--editor-border)',
                    backgroundColor: block.styles?.backgroundColor || '#18181b',
                  }}
                />
                <input
                  id="pi-background"
                  type="text"
                  className="editor-input"
                  value={block.styles?.backgroundColor || '#18181b'}
                  style={{ flex: 1, fontFamily: 'var(--editor-font-mono)' }}
                  disabled
                />
              </div>
            </div>
            {/* Fill color presets */}
            <div className="editor-row">
              <label className="editor-label">Quick Colors</label>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    title={preset.label}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      border: '1px solid rgba(255,255,255,0.1)',
                      background: preset.color,
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.2)')}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    onClick={() => {}}
                    disabled
                  />
                ))}
              </div>
            </div>
            <div className="editor-row">
              <label htmlFor="pi-opacity" className="editor-label">
                Opacity
              </label>
              <input
                id="pi-opacity"
                type="text"
                className="editor-input"
                value={String(block.styles?.opacity ?? 100)}
                style={{ fontFamily: 'var(--editor-font-mono)' }}
                disabled
              />
            </div>
          </div>
        )}

        {/* ── DATA BINDING ── */}
        {def?.entityType && (
          <>
            <SectionHeader
              title="Data Binding"
              open={!!expanded.binding}
              onToggle={() => toggle('binding')}
            />
            {expanded.binding && (
              <div className="editor-section-body">
                <div
                  style={{
                    padding: 8,
                    borderRadius: 'var(--editor-radius)',
                    border: '1px solid var(--editor-accent-dim)',
                    background: 'rgba(59,130,246,0.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--editor-font-size-xs)',
                      fontWeight: 600,
                      color: 'var(--editor-accent)',
                      marginBottom: 4,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {def.entityType} Data
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--editor-font-size-xs)',
                      color: 'var(--editor-text-dim)',
                    }}
                  >
                    {block.entityId
                      ? `Bound to ${def.entityType} #${block.entityId.slice(-6)}`
                      : 'Using default values (no entity linked)'}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      <div
        style={{
          padding: '8px var(--editor-panel-pad)',
          borderTop: '1px solid var(--editor-border)',
          display: 'flex',
          gap: 6,
        }}
      >
        <button
          className="editor-btn editor-btn-ghost editor-btn-sm"
          style={{ flex: 1 }}
          onClick={() => onDuplicateBlock(block.id)}
        >
          <Copy size={11} />
          <span>Duplicate</span>
        </button>
        <button
          className="editor-btn editor-btn-danger editor-btn-sm"
          style={{ flex: 1 }}
          onClick={() => onRemoveBlock(block.id)}
        >
          <Trash2 size={11} />
          <span>Delete</span>
        </button>
      </div>
    </div>
  )
}

/* ── Preset chip button ──────────────────────────────── */

function PresetChipButton({
  preset,
  isActive,
  onClick,
  size = 'md',
}: {
  preset: PresetChip
  isActive: boolean
  onClick: () => void
  size?: 'sm' | 'md'
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: size === 'sm' ? '2px 6px' : '3px 10px',
        borderRadius: 6,
        fontSize: size === 'sm' ? 9 : 10,
        fontFamily: 'var(--editor-font-mono)',
        fontWeight: 600,
        border: isActive ? '1px solid var(--editor-accent)' : '1px solid var(--editor-border-dim)',
        background: isActive
          ? 'color-mix(in srgb, var(--editor-accent, #3b82f6) 15%, transparent)'
          : 'transparent',
        color: isActive ? 'var(--editor-accent)' : 'var(--editor-text-dim)',
        cursor: 'pointer',
        transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
        letterSpacing: '0.02em',
      }}
    >
      {preset.color && (
        <span
          style={{
            width: size === 'sm' ? 6 : 8,
            height: size === 'sm' ? 6 : 8,
            borderRadius: '50%',
            background: preset.color,
            flexShrink: 0,
          }}
        />
      )}
      {preset.label}
    </button>
  )
}

/* ── Collapsible section header ────────────────────── */

function SectionHeader({
  title,
  open,
  onToggle,
  badge,
  icon,
}: {
  title: string
  open: boolean
  onToggle: () => void
  badge?: string
  icon?: React.ReactNode
}) {
  return (
    <button className="editor-section-header" onClick={onToggle}>
      <ChevronRight
        size={11}
        style={{
          color: 'var(--editor-text-dim)',
          transform: open ? 'rotate(90deg)' : undefined,
          transition: 'transform 0.15s ease',
        }}
      />
      {icon && <span style={{ color: 'var(--editor-accent)', flexShrink: 0 }}>{icon}</span>}
      <span className="editor-section-title">{title}</span>
      {badge && (
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 9,
            fontWeight: 500,
            color: badge === 'new' ? '#34d399' : 'var(--editor-accent)',
            letterSpacing: '0.03em',
          }}
        >
          {badge}
        </span>
      )}
    </button>
  )
}
